import React, { useEffect, useMemo } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { spacing } from '@rbilabs/universal-components/themes';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import ConfirmDialog from 'components/confirm-dialog';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { ProductCardListSkeleton } from 'components/product/skeleton';
import { toaster } from 'components/toaster/toaster';
import {
  GetUserFavoritesRefsDocument,
  GetUserReorderFavoritesDocument,
  useDeleteFavoriteMutation,
  useGetUserReorderFavoritesQuery,
} from 'generated/graphql-gateway';
import useFoodware from 'hooks/use-foodware';
import { isCartEntryReorderable } from 'hooks/use-reorderable-orders/utils';
import withMenuEmptyState from 'pages/menu/components/with-menu-empty-state';
import { useAuthContext } from 'state/auth';
import { useFavoritesContext } from 'state/favorites';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { EnableMenuReorderingAvailabilityOverrideVariation } from 'state/launchdarkly/variations';
import { routes } from 'utils/routing';

import { LoggedOutEmptyState, NoFavoritesEmptyState } from './empty-state';
import { FavoriteCard } from './favorite-card';
import { FavoriteCardData } from './types';

/**
 *
 * FavoritesCardList displays all the favorite orders and allows the guest to reorder, edit, and remove
 *
 */
const InnerFavoritesCardList = () => {
  const [removeFavoriteParams, setRemoveFavoriteParams] = React.useState<{
    id: string;
  } | null>(null);

  const { data, loading, refetch } = useGetUserReorderFavoritesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [deleteFavorite] = useDeleteFavoriteMutation({
    onCompleted: () => toaster.positive(t('removeFavoriteSucess')),
  });

  const { navigate } = useRouter();
  const { isAuthenticated } = useAuthContext();
  const { noStoreSelected } = useStoreContext();

  const { setFavorite, shouldRefetchFavorites, setShouldRefetchFavorites } = useFavoritesContext();

  const sanityIdsExcludedFromReordering = useFlag<
    EnableMenuReorderingAvailabilityOverrideVariation
  >(LaunchDarklyFlag.ENABLE_MENU_REORDERING_AVAILABILITY_OVERRIDE);

  const { isFoodware } = useFoodware();

  const handleEditFavorite = (id: string) =>
    navigate(noStoreSelected ? routes.storeLocator : `${routes.favorites}/${id}/edit`);

  const handleRemoveFavorite = (id: string) => {
    setRemoveFavoriteParams({ id });
  };

  const favorites = data?.userFavorites?.favorites;
  const shouldRefetch = !favorites || shouldRefetchFavorites;

  useEffect(() => {
    setFavorite(null);
  }, [favorites, setFavorite]);

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetchFavorites(false);
    }
  }, [refetch, setShouldRefetchFavorites, shouldRefetch]);

  const eligibleFavoriteOrders = useMemo(() => {
    return favorites?.reduce((favsAccum: FavoriteCardData[], favorite) => {
      const entries = (favorite?.entries || ([] as unknown)) as IBackendCartEntries[];

      const isFavoriteOrderReorderable = entries.some(entry =>
        isCartEntryReorderable(entry, sanityIdsExcludedFromReordering, isFoodware)
      );

      if (isFavoriteOrderReorderable) {
        favsAccum.push({
          id: favorite?._id || '',
          name: favorite?.name || '',
          entries,
        });
      }

      return favsAccum;
    }, []);
  }, [favorites, isFoodware, sanityIdsExcludedFromReordering]);

  const removeFavorite = () => {
    if (removeFavoriteParams?.id) {
      deleteFavorite({
        variables: {
          favoriteId: removeFavoriteParams.id,
        },
        refetchQueries: [
          { query: GetUserReorderFavoritesDocument },
          { query: GetUserFavoritesRefsDocument },
        ],
      });
    }
    setRemoveFavoriteParams(null);
  };

  if (loading) {
    return <ProductCardListSkeleton hasFooter />;
  }

  if (!eligibleFavoriteOrders?.length) {
    if (!isAuthenticated) {
      return <LoggedOutEmptyState />;
    }
    return <NoFavoritesEmptyState />;
  }

  return (
    <>
      {eligibleFavoriteOrders?.map((favorite, i) => {
        return (
          <FavoriteCard
            key={i}
            id={favorite.id}
            name={favorite.name}
            entries={favorite.entries}
            onEditFavorite={handleEditFavorite}
            onRemoveFavorite={handleRemoveFavorite}
          />
        );
      })}

      {!!removeFavoriteParams && (
        <ConfirmDialog
          showDialog
          heading={t('removeFavorite')}
          body={t('removeFavoriteConfirm')}
          cancelLabel={t('cancel')}
          confirmLabel={t('remove')}
          onCancel={() => setRemoveFavoriteParams(null)}
          onConfirm={removeFavorite}
          modalAppearanceEventMessage="Remove Favorite"
        />
      )}
    </>
  );
};

const FavoritesCardList = () => {
  return (
    <ScrollView testID="menu-favorites-page" style={{ marginTop: spacing.$1 }}>
      <CenteredRowContainer>
        <InnerFavoritesCardList />
      </CenteredRowContainer>
    </ScrollView>
  );
};

export default withMenuEmptyState(FavoritesCardList);
